/**
 * Protected route wrapper that redirects unauthenticated users to login
 * and enforces permission-based access control via PermissionContext.
 *
 * @module components/shared/ProtectedRoute
 * @see TASK-HLD-PERMISSION-ROUTES
 * @see ARCH-005-MOD-005
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { useAuth } from "../../store/AuthContext";
import { usePermissionContext } from "../../store/PermissionContext";

export interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If specified, user must hold this permission (checked via PermissionContext). */
  requiredPermission?: string;
  /** Path to redirect unauthenticated users to. Defaults to "/login". */
  loginPath?: string;
  /** Path to redirect unauthorized users to. Defaults to "/". */
  unauthorizedPath?: string;
}

/**
 * Wraps child routes; redirects to login if unauthenticated, or to
 * an unauthorized page if the user lacks the required permission.
 *
 * When `requiredPermission` is set, the component waits for the permission
 * fetch to complete before rendering (prevents premature redirects).
 */
export function ProtectedRoute({
  children,
  requiredPermission,
  loginPath = "/login",
  unauthorizedPath = "/",
}: ProtectedRouteProps): React.ReactElement {
  const { authenticated } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissionContext();
  const location = useLocation();

  // ── Unauthenticated → login ─────────────────────────────────────────
  if (!authenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // ── Wait for permissions to load (prevents premature redirect) ──────
  if (requiredPermission && permissionsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ── Permission check ───────────────────────────────────────────────
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={unauthorizedPath} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
